import { useCallback, useEffect, useRef } from "react";

/**
 * Tracks blob URLs created by a component so they can be revoked individually
 * (on replace) and collectively (on unmount). `revoke` is a no-op for URLs not
 * owned by this hook, which makes it safe to call on values that may also hold
 * remote URLs.
 */
export function useObjectUrls() {
  const urls = useRef<Set<string>>(new Set());

  const create = useCallback((blob: Blob) => {
    const url = URL.createObjectURL(blob);
    urls.current.add(url);
    return url;
  }, []);

  const revoke = useCallback((url: string | undefined) => {
    if (url && urls.current.delete(url)) URL.revokeObjectURL(url);
  }, []);

  useEffect(
    () => () => {
      urls.current.forEach(URL.revokeObjectURL);
      urls.current.clear();
    },
    [],
  );

  return { create, revoke };
}
